/**
 * frontend/src/lib/digGroups.js — Sept 25 batch item 9.
 *
 * The Live Music Archive often holds several tapes of one show (soundboard, audience, matrix,
 * different tapers). Dig groups them into ONE card with a recording picker.
 *
 *   group key = artist + show date, within one page of results
 *     artist  = the sorted SoundDAO artist ids on the item, else the archive `creator` (lower-cased)
 *     date    = item.date (YYYY-MM-DD)
 *
 * Only Live Music (etree) items are grouped: netlabel releases are distinct records even when an
 * artist puts out two on one day, and archive dates with no month/day are padded to -01-01.
 * Items with no artist or no date are never grouped. Order is preserved: a group sits where its
 * first recording appeared, and recordings keep the backend's order inside the group.
 */

export function groupKey(item) {
  if (!item || item.source !== 'etree' || !item.date) return null;
  const ids = (item.artists || []).map((a) => a.id).filter(Boolean).sort();
  const artist = ids.length ? `a:${ids.join('+')}` : (item.creator ? `c:${String(item.creator).trim().toLowerCase()}` : '');
  if (!artist) return null;
  return `${artist}|${String(item.date).slice(0, 10)}`;
}

/** groupRecordings(items) → [{ key, items: [item, …] }] in first-seen order. */
export function groupRecordings(items) {
  const groups = [];
  const byKey = new Map();
  for (const it of items || []) {
    const k = groupKey(it);
    if (k && byKey.has(k)) { byKey.get(k).items.push(it); continue; }
    const g = { key: k || `id:${it.identifier}`, items: [it] };
    if (k) byKey.set(k, g);
    groups.push(g);
  }
  return groups;
}

const KINDS = [
  [/(^|[._-])sbd([._-]|$)/i, 'Soundboard'],
  [/(^|[._-])(mtx|matrix)([._-]|$)/i, 'Matrix'],
  [/(^|[._-])(aud|audience)([._-]|$)/i, 'Audience'],
  [/(^|[._-])fm([._-]|$)/i, 'FM'],
];

/** "Soundboard · gd1977-05-08.sbd.hicks.4982" — a short, honest label for the picker. */
export function recordingLabel(item, index) {
  const id = String(item?.identifier || '');
  const kind = (KINDS.find(([re]) => re.test(id)) || [])[1];
  const short = id.length > 42 ? `${id.slice(0, 41)}…` : id;
  return [`Recording ${index + 1}`, kind, short].filter(Boolean).join(' · ');
}
